import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedSocialLinksWidget } from 'fym-common/src/db/serializersTypes';
import { LinkType } from 'fym-shared/src/smartlinks/linkTypes';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { moveItem } from '../blocks/useBlockOrder';
import { BlockAutosave } from './useBlockAutosave';
import { formMessages } from './formMessages';
import { socialMessages } from './socialMessages';
import { SocialProfileRow } from './SocialProfileRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for every block form */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialBlockFormProps {
  widget: SerializedSocialLinksWidget;
  autosave: BlockAutosave;
}

/**
 * The Social links block (design d6): the artist's profiles from Catalog connections and Promote
 * channels, in the artist's order, each with a switch. Saves `{ order, hidden }`.
 */
const SocialBlockFormComponent: React.FC<SocialBlockFormProps> = ({ widget, autosave }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const [links, setLinks] = useState(() => widget.data.allSocialLinks ?? widget.data.socialLinks);
  const [hidden, setHidden] = useState<LinkType[]>(() => widget.data.hiddenTypes ?? []);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const { save } = autosave;

  const commit = useCallback(
    (nextLinks: typeof links, nextHidden: LinkType[]) => {
      // Two profiles can have one link type. The API refuses a repeated type, so keep the first one.
      const order = [...new Set(nextLinks.map((link) => link.type))];
      save({ order, hidden: nextHidden });
    },
    [save]
  );

  const onToggle = useCallback(
    (type: LinkType, on: boolean) => {
      const next = on ? hidden.filter((one) => one !== type) : [...hidden.filter((one) => one !== type), type];
      setHidden(next);
      commit(links, next);
    },
    [commit, hidden, links]
  );
  const move = useCallback(
    (from: number, to: number) => {
      if (to < 0 || to >= links.length || from === to) {
        return;
      }
      const next = moveItem(links, from, to);
      setLinks(next);
      commit(next, hidden);
    },
    [commit, hidden, links]
  );
  const onDragStart = useCallback((index: number) => setDragFrom(index), []);
  const onDragOver = useCallback((index: number, event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(index);
  }, []);
  const onDrop = useCallback(() => {
    if (dragFrom !== null && dragOver !== null) {
      move(dragFrom, dragOver);
    }
    setDragFrom(null);
    setDragOver(null);
  }, [dragFrom, dragOver, move]);
  const onKeyMove = useCallback((index: number, delta: number) => move(index, index + delta), [move]);
  const goToCatalog = useCallback(() => router.push(SMLRoutes.catalog), [router]);
  const goToChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);

  const buttons = (
    <div className={styles.buttonRow}>
      <button type="button" className={styles.linkButton} onClick={goToChannels}>
        {intl.formatMessage(socialMessages.openChannels)}
      </button>
      <button type="button" className={styles.linkButton} onClick={goToCatalog}>
        {intl.formatMessage(socialMessages.openCatalog)}
      </button>
    </div>
  );

  if (links.length === 0) {
    return (
      <div className={styles.form} data-testid={`form-${SmartLinkWidgetType.socialLinks}`}>
        <p className={`${styles.note} ${styles.notePlain}`}>{intl.formatMessage(socialMessages.empty)}</p>
        {buttons}
      </div>
    );
  }

  return (
    <div className={styles.form} data-testid={`form-${SmartLinkWidgetType.socialLinks}`}>
      <div className={styles.field}>
        <span className={styles.label}>{intl.formatMessage(formMessages.socialProfiles)}</span>
        <div className={styles.optionList}>
          {links.map((link, index) => (
            <SocialProfileRow
              key={`${link.type}-${link.url}`}
              link={link}
              index={index}
              isOn={!hidden.includes(link.type)}
              onToggle={onToggle}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onKeyMove={onKeyMove}
            />
          ))}
        </div>
        <p className={styles.help}>{intl.formatMessage(formMessages.socialNewProfiles)}</p>
      </div>
      {buttons}
    </div>
  );
};

export const SocialBlockForm = TypedMemo(SocialBlockFormComponent);
